export const ACCESS_LEVELS = Object.freeze({
  NONE: "NONE",
  TEAM: "TEAM",
  BRANCH: "BRANCH",
  ALL: "ALL",
});

export const ALL_BRANCH = Object.freeze({
  branchName: "ALL",
  branchId: -2,
});

export const ALL_TEAM = Object.freeze({
  teamName: "ALL",
  teamID: -2,
});

export const ALL_FEE_EARNER = Object.freeze({
  display: "ALL",
  userRef: "ALL",
  active: null,
});

const isTrue = (value) => value === true;
const isFalse = (value) => value === false;

const isSupportedAccessCombination = (allowAccessData) => {
  const branches = allowAccessData?.accessAllBranches;
  const teams = allowAccessData?.accessAllTeams;
  const feeEarners = allowAccessData?.accessAllFeeEarnersInTeam;

  return (
    (isFalse(branches) && isFalse(teams) && isFalse(feeEarners)) ||
    (isFalse(branches) && isFalse(teams) && isTrue(feeEarners)) ||
    (isFalse(branches) && isTrue(teams) && isTrue(feeEarners)) ||
    (isTrue(branches) && isTrue(teams) && isTrue(feeEarners))
  );
};

export const getAccessLevel = (allowAccessData) => {
  if (!isSupportedAccessCombination(allowAccessData)) {
    return ACCESS_LEVELS.NONE;
  }

  if (allowAccessData.accessAllBranches) return ACCESS_LEVELS.ALL;
  if (allowAccessData.accessAllTeams) return ACCESS_LEVELS.BRANCH;
  if (allowAccessData.accessAllFeeEarnersInTeam) return ACCESS_LEVELS.TEAM;
  return ACCESS_LEVELS.NONE;
};

const dedupeBy = (items, getKey) => {
  const seen = new Set();

  return items.filter((item) => {
    const value = getKey(item);
    if (value === null || value === undefined || value === "") return false;

    if (seen.has(value)) return false;

    seen.add(value);
    return true;
  });
};

const realBranches = (branches) =>
  (Array.isArray(branches) ? branches : []).filter(
    (branch) =>
      branch?.branchId !== -2 &&
      String(branch?.branchName || "").toUpperCase() !== "ALL",
  );

const teamsWithParents = (branches) =>
  realBranches(branches).flatMap((branch) =>
    (Array.isArray(branch?.teams) ? branch.teams : []).map((team) => ({
      branch,
      team,
    })),
  );

const isRealTeam = (team) =>
  team?.teamID !== -2 &&
  String(team?.teamName || "").toUpperCase() !== "ALL";

const isRealFeeEarner = (feeEarner) =>
  feeEarner?.userRef &&
  String(feeEarner.userRef).toUpperCase() !== "ALL";

export const buildBranchOptions = (branches) => [
  ALL_BRANCH,
  ...dedupeBy(realBranches(branches), (branch) => branch.branchId),
];

export const buildTeamOptions = (
  branches,
  selectedBranchId,
  accessLevel,
) => {
  const shouldFilterByBranch =
    accessLevel === ACCESS_LEVELS.ALL &&
    selectedBranchId !== ALL_BRANCH.branchId;

  const teams = teamsWithParents(branches)
    .filter(({ branch, team }) => {
      if (!isRealTeam(team)) return false;
      if (!shouldFilterByBranch) return true;

      const branchId = branch?.branchId ?? team?.branchId;
      return branchId === selectedBranchId;
    })
    .map(({ team }) => team);

  return [ALL_TEAM, ...dedupeBy(teams, (team) => team.teamID)];
};

export const buildFeeEarnerOptions = (
  branches,
  selectedTeamId,
  accessLevel,
) => {
  const shouldFilterByTeam =
    (accessLevel === ACCESS_LEVELS.BRANCH ||
      accessLevel === ACCESS_LEVELS.ALL) &&
    selectedTeamId !== ALL_TEAM.teamID;

  const feeEarners = teamsWithParents(branches)
    .filter(({ team }) => {
      if (!isRealTeam(team)) return false;
      return !shouldFilterByTeam || team.teamID === selectedTeamId;
    })
    .flatMap(({ team }) =>
      Array.isArray(team?.feeEarners) ? team.feeEarners : [],
    )
    .filter(isRealFeeEarner)
    .map((feeEarner) => ({
      ...feeEarner,
      display: feeEarner.displayname
        ? `${feeEarner.userRef} - ${feeEarner.displayname}`
        : feeEarner.userRef,
    }));

  return [
    ALL_FEE_EARNER,
    ...dedupeBy(feeEarners, (feeEarner) => feeEarner.userRef),
  ];
};

const findOptionByKey = (options, key, value) =>
  (Array.isArray(options) ? options : []).find((option) =>
    option?.[key] === value,
  );

export const normalizeFeeEarnerSelection = (
  options,
  currentSelection,
  currentUser,
) =>
  findOptionByKey(options, "userRef", currentSelection?.userRef) ||
  findOptionByKey(options, "userRef", currentUser?.userRef) ||
  ALL_FEE_EARNER;
